import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * YUMIX — Base Input.
 *
 * Special handling for type="number" to fix the global "stuck 0" frustration:
 *
 * While the field is FOCUSED, the input uses its own internal local value.
 * The parent's `value` prop is only re-synced on blur or when the parent
 * explicitly changes it from outside (detected via a hash of the value).
 * This decoupling allows the input to show an empty string even when the
 * parent holds `0` and uses `onChange={e => setX(parseFloat(e.target.value) || 0)}`
 * — the classic pattern that was blocking every caller in the codebase.
 *
 * Behavior:
 *   · onFocus: if parent value is "0"/0, the field clears visually (no forced 0)
 *              and selects anything else for easy overwrite
 *   · onChange: forwarded verbatim so parents keep their existing logic
 *   · onBlur: if left empty, the field re-displays the parent's canonical
 *             value (typically 0 after `|| 0`) so downstream calculations
 *             never see NaN or empty
 *
 * For non-number inputs this component behaves identically to the
 * original shadcn Input (zero behavioral change).
 */
const Input = React.forwardRef(({ className, type, value, onFocus, onChange, onBlur, defaultValue, ...props }, ref) => {
  const isNumber = type === "number";
  const [isFocused, setIsFocused] = React.useState(false);
  const [localValue, setLocalValue] = React.useState(value ?? "");

  // Iter. 411.mm14 — CRITICAL Safari iOS crash fix.
  // Previously this effect ran for EVERY input type, including text/email
  // fields. On text inputs `isFocused` never flipped to true (handleFocus
  // only sets it for number inputs), so `!isFocused` was always truthy →
  // `setLocalValue(value)` fired on every parent re-render caused by the
  // user's keystroke → an extra render per keystroke.
  // On desktop Chrome this is invisible, but on iOS Safari with the
  // virtual keyboard animation triggering viewport events, the extra
  // render tipped WebKit's "runaway render" heuristic and killed the tab
  // ("Un problème récurrent est survenu sur .../dashboard").
  // Fix: only run this sync loop for NUMBER inputs, where localValue is
  // actually consumed (see `displayedValue` below).
  React.useEffect(() => {
    if (!isNumber) return;
    if (!isFocused) setLocalValue(value ?? "");
  }, [value, isFocused, isNumber]);

  const handleFocus = React.useCallback((e) => {
    if (isNumber) {
      setIsFocused(true);
      const current = String(e.target.value ?? "");
      if (current === "0") {
        // Visually clear without touching the parent's state
        setLocalValue("");
      } else {
        try { e.target.select(); } catch (_) { /* ignore */ }
      }
    }
    if (onFocus) onFocus(e);
  }, [isNumber, onFocus]);

  const handleChange = React.useCallback((e) => {
    if (isNumber) setLocalValue(e.target.value);
    if (onChange) onChange(e);
  }, [isNumber, onChange]);

  const handleBlur = React.useCallback((e) => {
    if (isNumber) {
      // If user left the field empty, emit a synthetic "0" change upward
      // so macros/totals calculations never break.
      if (e.target.value === "" && onChange) {
        const syntheticEvent = {
          ...e,
          target: Object.assign(e.target, { value: "0" }),
          currentTarget: Object.assign(e.currentTarget, { value: "0" }),
        };
        onChange(syntheticEvent);
      }
      setIsFocused(false);
      // Resync with whatever the parent ends up with (next render cycle)
    }
    if (onBlur) onBlur(e);
  }, [isNumber, onChange, onBlur]);

  // For number inputs, render our local value during focus; otherwise the
  // parent's value. For all other input types, render `value` directly
  // (unchanged from the original shadcn Input).
  const displayedValue = isNumber
    ? (isFocused ? localValue : (value ?? ""))
    : value;

  return (
    <input
      type={type}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      {...(isNumber
        ? { value: displayedValue }
        : (value !== undefined ? { value } : { defaultValue }))}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      {...props} />
  );
})
Input.displayName = "Input"

export { Input }
